import { useEffect, useMemo, useState } from 'react';
import { demeterPageElementsApi } from '../../../../Apis/Apis';
import { DemeterPageElementModel } from '../../../../Generated/Raven-Demeter';
import useApiWithoutAutoExecute from '../../../Apis/Hooks/useApiWithoutAutoExecute';
import useLanguage from '../../../Services/Language/useLanguageHook';
import { TrackingEvent } from '../../../Services/Tracking/TrackingService';
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner';
import { ITreeNode } from '../../TreeView/TreeItem';
import TreeView from '../../TreeView/TreeView';
import styles from './PageElementModal.module.scss';

interface IPageElementSelectedProps {
    selectedPageElement: DemeterPageElementModel | null | undefined;
    pageElements: DemeterPageElementModel[];
    hideImage?: boolean;
    handlePageElementSelect: (selected: DemeterPageElementModel | null | undefined) => void;
    track: TrackingEvent;
}

const PageElementSelected: React.FC<IPageElementSelectedProps> = (props) => {
    const [translations, translate] = useLanguage();
    const [imageSrc, setImageSrc] = useState<string | null>(null);
    const [imageData, setImageData] = useState<any | null>(null);
    const [imageContentType, setImageContentType] = useState<string | null>(null);
    const [selectedPageElement, setSelectedPageElement] = useState<DemeterPageElementModel | null>();

    const [loadingImage, getImage, imageResponse, imageError] = useApiWithoutAutoExecute(
        () => {
            if (!selectedPageElement?.demeterPageElementGuid) {
                return null;
            }

            return demeterPageElementsApi
                .getDemeterPageElementImage(selectedPageElement.demeterPageElementGuid, {
                    responseType: 'arraybuffer',
                })
                .then((response) => {
                    setImageContentType(response.headers['content-type']);
                    setImageData(response.data);
                    return response;
                });
        },
        {
            stopGlobalErrorHandling: true,
        },
    );

    const treeNodes: ITreeNode[] = useMemo(
        () =>
            props.pageElements.map((pageElement, index) => ({
                id: pageElement.demeterPageElementGuid,
                name:
                    pageElement.pageElementType && translations.pageElementType[pageElement.pageElementType]
                        ? translations.pageElementType[pageElement.pageElementType]
                        : (pageElement.displayName ? translate(pageElement.displayName) : null) ?? pageElement.pageElementType ?? '',
                value: pageElement.demeterPageElementGuid,
                parentId: '',
                parentValue: '',
                column: 'left',
                selectedProduct: selectedPageElement?.demeterPageElementGuid === pageElement.demeterPageElementGuid,
                expanded: false,
                order: index,
                displayOrder: index,
                children: [],
            })),
        [props.pageElements, selectedPageElement],
    );

    useEffect(() => {
        if (props.selectedPageElement) {
            setSelectedPageElement(props.selectedPageElement);
            props.handlePageElementSelect(props.selectedPageElement);
            getImage();
        }
    }, [props.selectedPageElement]);

    const handleSelect = (event: React.MouseEvent<HTMLElement>) => {
        const { id } = event.currentTarget;
        const node = treeNodes.find((n) => n.id === id);
        if (!node) {
            return;
        }

        const pageElement = props.pageElements.find((p) => p.demeterPageElementGuid === node.id);
        if (!pageElement) {
            return;
        }

        setSelectedPageElement(pageElement);
        props.handlePageElementSelect(pageElement);
        getImage();
    };

    useEffect(() => {
        setImageSrc(null);
    }, [props.pageElements]);

    useEffect(() => {
        if (imageData && imageContentType) {
            const blob = new Blob([imageData], { type: imageContentType });
            const url = URL.createObjectURL(blob);
            setImageSrc(url);
            return () => URL.revokeObjectURL(url);
        }
        return () => {};
    }, [imageData, imageContentType]);

    useEffect(() => {
        if (imageError) {
            setImageSrc(null);
        }
    }, [imageError]);

    return (
        <>
            <div className={styles.page_element_modal_selected_nodes}>
                <TreeView data={treeNodes} searchTerm="" handleSelect={handleSelect} disabledNodes={[]} track={props.track} />
            </div>
            {!props.hideImage && (
                <div className={styles.page_element_modal_selected_image}>
                    {!imageSrc && loadingImage && <LoadingSpinner />}
                    {imageSrc && <img src={imageSrc} alt="" />}
                </div>
            )}
        </>
    );
};

export default PageElementSelected;
